// =============================================================================
// Module Project - Repository: Workload
// Gestion de la charge et capacité des collaborateurs
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

const WORKLOAD_ALLOCATION_TOLERANCE_HOURS = 0.05;

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface MemberCapacity {
  memberId: string;
  userId: string;
  fullName: string | null;
  avatarUrl: string | null;
  email: string;
  weeklyCapacityHours: number;
  overrideHours: number | null;
  effectiveCapacity: number;
}

export interface MemberWorkload {
  memberId: string;
  userId: string;
  fullName: string | null;
  avatarUrl: string | null;
  email: string;
  capacityHours: number;
  allocatedHours: number;
  timeSpentHours: number;
  remainingWorkHours: number;
  availableHours: number;
  utilizationPercent: number;
  // Breakdown by project
  projectBreakdown: ProjectWorkloadBreakdown[];
  // Task details for drill-down
  taskDetails: TaskWorkloadDetail[];
  // V2: Tâches nécessitant une répartition (multi-assignés sans allocation)
  tasksNeedingAllocation: TaskNeedingAllocation[];
  // V4: Heures allouées sur d'autres projets (sans détails pour confidentialité)
  externalAllocatedHours: number;
}

export interface ProjectWorkloadBreakdown {
  projectId: string;
  projectName: string;
  allocatedHours: number;
  timeSpentHours: number;
  remainingHours: number;
  taskCount: number;
}

export interface TaskWorkloadDetail {
  taskId: string;
  taskTitle: string;
  projectId: string;
  projectName: string;
  estimatedHours: number;
  timeSpentHours: number;
  remainingHours: number;
  dueAt: string | null;
  statusCode: string;
  assigneeCount: number;
  // V2: Heures imputées à ce membre pour cette tâche
  imputedHours: number;
  // V2: Est-ce que cette tâche a des allocations manuelles?
  hasManualAllocation: boolean;
  // V2.1: Est-ce que la tâche est terminée?
  isCompleted: boolean;
  // V3.3: Total des heures allouées pour cette tâche (toutes assignés confondus)
  totalAllocatedHours: number;
}

// V2: Tâche multi-assignée nécessitant répartition
export interface TaskNeedingAllocation {
  taskId: string;
  taskTitle: string;
  projectId: string;
  projectName: string;
  remainingHours: number;
  assigneeCount: number;
  dueAt: string | null;
}

export interface WorkloadAllocation {
  id: string;
  orgId: string;
  taskId: string;
  memberId: string;
  allocatedHours: number;
  weekStart: string;
}

export interface TeamWorkloadSummary {
  totalCapacityHours: number;
  totalAllocatedHours: number;
  totalRemainingWorkHours: number;
  overloadedMembers: number;
  underutilizedMembers: number;
  optimalMembers: number;
  averageUtilization: number;
  // V2: Nombre de tâches multi-assignées sans répartition
  tasksNeedingAllocationCount: number;
  // V2.2: Total heures à répartir
  hoursNeedingAllocation: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function getWeekStart(date: Date = new Date()): string {
  const d = new Date(date);
  // Ajuster au fuseau local pour éviter les décalages UTC
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  // Retourner en format YYYY-MM-DD sans conversion UTC
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
}

export function getWeekEnd(weekStart: string): string {
  const [year, month, day] = weekStart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + 6);
  const endYear = d.getFullYear();
  const endMonth = String(d.getMonth() + 1).padStart(2, '0');
  const endDay = String(d.getDate()).padStart(2, '0');
  return `${endYear}-${endMonth}-${endDay}`;
}

export function getWeekRangeLabel(weekStart: string): string {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  
  const formatDate = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `${formatDate(start)} - ${formatDate(end)}`;
}

export function getUtilizationStatus(percent: number): 'low' | 'optimal' | 'high' | 'overloaded' {
  if (percent < 50) return 'low';
  if (percent <= 80) return 'optimal';
  if (percent <= 100) return 'high';
  return 'overloaded';
}

export function getUtilizationColor(percent: number): string {
  const status = getUtilizationStatus(percent);
  switch (status) {
    case 'low': return 'bg-muted';
    case 'optimal': return 'bg-emerald-500';
    case 'high': return 'bg-amber-500';
    case 'overloaded': return 'bg-red-500';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROJECT MEMBERS WITH CAPACITY
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchProjectMembersCapacity(
  projectId: string,
  orgId: string,
  weekStart: string
): Promise<MemberCapacity[]> {
  // Get project members with their org member info (using new RPC)
  let memberIds: string[] = [];
  
  const { data: membersData, error: pmError } = await supabase.rpc('get_project_members', {
    _project_id: projectId,
  });

  if (!pmError && membersData && membersData.length > 0) {
    memberIds = (membersData as any[]).map((m: any) => m.org_member_id);
  } else {
    // Fallback: query project_permissions + space_members directly
    console.warn('[Workload] get_project_members RPC failed or empty, using fallback:', pmError?.message);
    
    const seenIds = new Set<string>();
    
    // 1. Direct project permissions
    const { data: directPerms } = await supabase
      .from('project_permissions')
      .select('org_member_id')
      .eq('project_id', projectId);
    
    (directPerms || []).forEach(p => seenIds.add(p.org_member_id));
    
    // 2. Space inherited members
    const { data: project } = await supabase
      .from('project_projects')
      .select('space_id')
      .eq('id', projectId)
      .single();
    
    if (project?.space_id) {
      const { data: spaceMembers } = await supabase
        .from('space_members')
        .select('org_member_id')
        .eq('space_id', project.space_id);
      
      (spaceMembers || []).forEach(sm => seenIds.add(sm.org_member_id));
    }
    
    // NOTE: Owner/Admin du workspace ne sont PAS auto-inclus.
    // Ils n'apparaissent dans les vues Charge / Disponibilité / Charge planning
    // que s'ils sont ajoutés explicitement (project_permissions) ou hérités
    // d'un space. Leur accès reste implicite mais n'entre pas dans les calculs.

    memberIds = Array.from(seenIds);
  }
  
  if (memberIds.length === 0) return [];

  // Get org member details with capacity
  
  const { data: orgMembers } = await supabase
    .from('core_organization_members')
    .select(`
      id,
      user_id,
      weekly_capacity_hours,
      core_profiles!inner (
        full_name,
        avatar_url,
        email
      )
    `)
    .in('id', memberIds);

  // Get capacity overrides for this week
  const orgMemberIds = (orgMembers || []).map((m: any) => m.id);
  
  const { data: overrides, error: ovError } = await supabase
    .from('core_member_capacity_overrides')
    .select('member_id, override_hours')
    .in('member_id', orgMemberIds)
    .eq('week_start', weekStart);

  if (ovError) {
    console.error('Error fetching capacity overrides:', ovError);
  }

  const overrideMap = new Map((overrides || []).map(o => [o.member_id, o.override_hours]));

  return (orgMembers || []).map((member: any) => {
    const profile = member.core_profiles;
    const baseCapacity = member.weekly_capacity_hours ?? 35;
    const override = overrideMap.get(member.id) ?? null;

    return {
      memberId: member.id,
      userId: member.user_id,
      fullName: profile.full_name,
      avatarUrl: profile.avatar_url,
      email: profile.email,
      weeklyCapacityHours: baseCapacity,
      overrideHours: override,
      effectiveCapacity: override ?? baseCapacity,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// V2: FETCH MEMBER WORKLOAD (ALL PROJECTS) - WITH AUTO IMPUTATION FOR SINGLE ASSIGNEE
// V6: Added optional currentProjectId to calculate external hours for cross-project consistency
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchMemberWorkload(
  memberId: string,
  orgId: string,
  weekStart: string,
  currentProjectId?: string, // V6: Optional - if provided, calculates external hours for consistency
  readOnly?: boolean // V3.7: Skip auto-allocation writes when user has no management rights
): Promise<MemberWorkload | null> {
  // Get member info with capacity
  const { data: member, error: memberError } = await supabase
    .from('core_organization_members')
    .select(`
      id,
      user_id,
      weekly_capacity_hours,
      core_profiles!inner (
        full_name,
        avatar_url,
        email
      )
    `)
    .eq('id', memberId)
    .single();

  if (memberError || !member) {
    console.error('Error fetching member:', memberError);
    return null;
  }

  const profile = member.core_profiles as any;
  const weekEnd = getWeekEnd(weekStart);

  // Get capacity override if exists
  const { data: override } = await supabase
    .from('core_member_capacity_overrides')
    .select('override_hours')
    .eq('member_id', memberId)
    .eq('week_start', weekStart)
    .maybeSingle();

  const capacityHours = override?.override_hours ?? member.weekly_capacity_hours ?? 35;

  // V7: If currentProjectId is provided, calculate GLOBAL external charge
  // This includes both DB allocations AND auto-imputed solo tasks from other projects
  let externalAllocatedHours = 0;
  if (currentProjectId) {
    // Step 1: Get DB allocations from other projects
    // V8: Include status_code to filter out completed tasks
    const { data: externalAllocations } = await supabase
      .from('project_task_workload_allocations')
      .select(`
        allocated_hours,
        task_id,
        project_tasks!inner (
          project_id,
          status_code
        )
      `)
      .eq('org_member_id', memberId)
      .eq('week_start', weekStart)
      .neq('project_tasks.project_id', currentProjectId);
    
    // Get terminal status codes for filtering external allocations
    const { data: terminalStatusesForExternal } = await supabase
      .from('project_task_statuses')
      .select('code')
      .eq('org_id', orgId)
      .eq('is_terminal', true);
    const terminalCodesForExternal = new Set((terminalStatusesForExternal || []).map(s => s.code));
    
    // V8: Filter out completed tasks from external charge calculation
    const externalDbHours = (externalAllocations || []).reduce(
      (sum: number, ea: any) => {
        const taskStatus = ea.project_tasks?.status_code;
        if (taskStatus && terminalCodesForExternal.has(taskStatus)) {
          return sum; // Skip completed tasks
        }
        return sum + (ea.allocated_hours || 0);
      }, 
      0
    );
    const externalAllocatedTaskIds = new Set(
      (externalAllocations || [])
        .filter((ea: any) => {
          const taskStatus = ea.project_tasks?.status_code;
          return !taskStatus || !terminalCodesForExternal.has(taskStatus);
        })
        .map((ea: any) => ea.task_id)
    );
    
    // Step 2: Get solo tasks from other projects that might not have DB allocations yet
    // These need auto-imputation to be consistent with the local calculation
    const { data: externalSoloTasks } = await supabase
      .from('project_task_assignees')
      .select(`
        task_id,
        project_tasks!inner (
          id,
          project_id,
          estimated_hours,
          status_code
        )
      `)
      .eq('org_member_id', memberId)
      .neq('project_tasks.project_id', currentProjectId);
    
    // Get terminal status codes for filtering
    const { data: externalStatuses } = await supabase
      .from('project_task_statuses')
      .select('code')
      .eq('org_id', orgId)
      .eq('is_terminal', true);
    const externalTerminalCodes = new Set((externalStatuses || []).map(s => s.code));
    
    // Get time entries for external tasks to calculate remaining hours
    const externalTaskIds = (externalSoloTasks || []).map((t: any) => t.task_id);
    const { data: externalTimeEntries } = await supabase
      .from('project_task_time_entries')
      .select('task_id, duration_minutes')
      .in('task_id', externalTaskIds.length > 0 ? externalTaskIds : ['00000000-0000-0000-0000-000000000000']);
    
    const externalTimeMap = new Map<string, number>();
    (externalTimeEntries || []).forEach(te => {
      externalTimeMap.set(te.task_id, (externalTimeMap.get(te.task_id) || 0) + te.duration_minutes);
    });
    
    // Get assignee counts for external tasks
    const { data: externalAssigneeCounts } = await supabase
      .from('project_task_assignees')
      .select('task_id')
      .in('task_id', externalTaskIds.length > 0 ? externalTaskIds : ['00000000-0000-0000-0000-000000000000']);
    
    const externalAssigneeCountMap = new Map<string, number>();
    (externalAssigneeCounts || []).forEach(a => {
      externalAssigneeCountMap.set(a.task_id, (externalAssigneeCountMap.get(a.task_id) || 0) + 1);
    });
    
    // Calculate auto-imputed hours for solo tasks without DB allocations
    let externalSoloAutoImputed = 0;
    for (const task of (externalSoloTasks || [])) {
      const t = task.project_tasks as any;
      if (externalTerminalCodes.has(t.status_code)) continue;
      if (externalAllocatedTaskIds.has(task.task_id)) continue; // Already counted in DB
      
      const assigneeCount = externalAssigneeCountMap.get(task.task_id) || 1;
      if (assigneeCount === 1) {
        // Solo task - auto-impute remaining hours
        const timeSpent = (externalTimeMap.get(task.task_id) || 0) / 60;
        const remaining = Math.max(0, (t.estimated_hours || 0) - timeSpent);
        externalSoloAutoImputed += remaining;
      }
      // Multi-assignee without allocation = 0 (per rules: not confirmed)
    }
    
    externalAllocatedHours = externalDbHours + externalSoloAutoImputed;
  }

  // V6: Get tasks - filter by project if context provided (security + privacy)
  let assignedTasksQuery = supabase
    .from('project_task_assignees')
    .select(`
      task_id,
      org_id,
      project_tasks!inner (
        id,
        title,
        project_id,
        estimated_hours,
        due_at,
        status_code,
        created_at,
        project_projects!inner (
          id,
          name
        )
      )
    `)
    .eq('org_member_id', memberId)
    .eq('org_id', orgId);
  
  if (currentProjectId) {
    assignedTasksQuery = assignedTasksQuery.eq('project_tasks.project_id', currentProjectId);
  }

  const { data: assignedTasks, error: tasksError } = await assignedTasksQuery;

  if (tasksError) {
    console.error('Error fetching assigned tasks:', tasksError);
    return null;
  }

  // V2.3: Get allocations for this member for this week to include tasks with allocations
  const { data: weekAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id')
    .eq('org_member_id', memberId)
    .eq('week_start', weekStart);
  
  const tasksWithAllocationThisWeek = new Set((weekAllocations || []).map(a => a.task_id));

  // V2.4: PERF - Get ALL time entries for all assigned tasks in ONE aggregated query (avoid N+1)
  // This is the SINGLE SOURCE OF TRUTH for time spent on tasks
  const taskIds = (assignedTasks || []).map((at: any) => at.project_tasks.id);
  const { data: allTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000']);
  
  // Aggregate time spent TOTAL per task from time_entries (not just this week)
  // V2.4: remainingHours = estimation - temps passé TOTAL sur la tâche
  const taskTimeSpentMap = new Map<string, number>();
  (allTimeEntries || []).forEach(te => {
    taskTimeSpentMap.set(te.task_id, (taskTimeSpentMap.get(te.task_id) || 0) + te.duration_minutes);
  });

  // V2.4: PERF - Get ALL assignee counts in ONE query (avoid N+1)
  const { data: assigneeCounts } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000']);
  
  // Aggregate assignee count per task
  const taskAssigneeCountMap = new Map<string, number>();
  (assigneeCounts || []).forEach(ac => {
    taskAssigneeCountMap.set(ac.task_id, (taskAssigneeCountMap.get(ac.task_id) || 0) + 1);
  });

  // V2.6: Get tasks that have time entries this week (to include in workload view)
  const { data: timeEntriesForTasksThisWeek } = await supabase
    .from('project_task_time_entries')
    .select('task_id')
    .eq('user_id', member.user_id)
    .gte('logged_at', weekStart)
    .lte('logged_at', weekEnd + 'T23:59:59Z');
  
  const tasksWithTimeEntriesThisWeek = new Set(
    (timeEntriesForTasksThisWeek || []).map(te => te.task_id)
  );

  // Filter tasks relevant to the displayed week:
  // V2.6 Règle d'inclusion (améliorée):
  // 1. Task has allocation for this week, OR
  // 2. Task has time entries logged this week, OR
  // 3. Task has due_at within the week range (starts before week end AND ends after week start), OR
  // 4. Task has no due_at but was created before week end (ongoing tasks)
  // 5. Single-assignee task with due date after week start (work can start before due date)
  const filteredTasks = (assignedTasks || []).filter((at: any) => {
    const task = at.project_tasks;
    
    // Rule 1: Task has allocation for this week
    if (tasksWithAllocationThisWeek.has(task.id)) {
      return true;
    }

    // Rule 2: Task has time entries this week (member is actively working on it)
    if (tasksWithTimeEntriesThisWeek.has(task.id)) {
      return true;
    }
    
    const dueAt = task.due_at;
    const weekStartDate = new Date(weekStart);
    const weekEndDate = new Date(weekEnd + 'T23:59:59Z');
    
    // Rule 3 & 5: If task has a due date, include if due date is after week start
    // (work can start any time before the deadline)
    if (dueAt) {
      const dueDate = new Date(dueAt);
      // Include if due date hasn't passed the week start (task is still relevant)
      return dueDate >= weekStartDate;
    }
    
    // Rule 4: If no due date, include if task was created before week end (ongoing tasks)
    const createdAt = new Date(task.created_at);
    return createdAt <= weekEndDate;
  });

  // Get terminal status codes to filter out completed tasks
  const { data: terminalStatuses } = await supabase
    .from('project_task_statuses')
    .select('code')
    .eq('org_id', orgId)
    .eq('is_terminal', true);

  const terminalCodes = new Set((terminalStatuses || []).map(s => s.code));

  // Get ALL manual allocations for this member for this week (for multi-project capacity)
  const { data: allAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id, allocated_hours')
    .eq('org_member_id', memberId)
    .eq('week_start', weekStart);

  const allocationMap = new Map((allAllocations || []).map(a => [a.task_id, a.allocated_hours]));

  // V3: PERF - Pre-fetch ALL allocations for all tasks in ONE query (avoid N+1 inside loop)
  // This gets the total allocated hours per task from all assignees
  const { data: allTaskAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id, allocated_hours')
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000'])
    .eq('week_start', weekStart);
  
  // Aggregate total allocations per task (sum of all assignees' allocations)
  const taskTotalAllocationsMap = new Map<string, number>();
  (allTaskAllocations || []).forEach(a => {
    taskTotalAllocationsMap.set(a.task_id, (taskTotalAllocationsMap.get(a.task_id) || 0) + a.allocated_hours);
  });

  // Get time entries for this week ONLY (for display: timeSpentThisWeek)
  const { data: timeEntriesThisWeek } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .eq('user_id', member.user_id)
    .gte('logged_at', weekStart)
    .lte('logged_at', weekEnd + 'T23:59:59Z');

  // This is for display only (timeSpentThisWeek), NOT for remaining work calculation
  const timeEntryThisWeekMap = new Map<string, number>();
  (timeEntriesThisWeek || []).forEach(te => {
    timeEntryThisWeekMap.set(te.task_id, (timeEntryThisWeekMap.get(te.task_id) || 0) + te.duration_minutes);
  });

  // Calculate totals with V2 logic
  let totalImputed = 0; // Charge imputée à ce membre
  let totalTimeSpent = 0;
  let totalRemaining = 0;
  const projectMap = new Map<string, ProjectWorkloadBreakdown>();
  const taskDetails: TaskWorkloadDetail[] = [];
  const tasksNeedingAllocation: TaskNeedingAllocation[] = [];

  for (const at of filteredTasks) {
    const task = at.project_tasks as any;
    const project = task.project_projects;
    
    // Skip completed tasks for remaining work calculation
    const isCompleted = terminalCodes.has(task.status_code);
    
    const estimatedHours = task.estimated_hours || 0;
    
    // V2.3: SINGLE SOURCE OF TRUTH - utiliser project_task_time_entries
    // Le temps passé total de la tâche vient de la somme des time entries (pas time_spent_minutes)
    const totalTaskTimeSpentMinutes = taskTimeSpentMap.get(task.id) || 0;
    const taskTimeSpentHours = totalTaskTimeSpentMinutes / 60;
    const taskRemainingHours = isCompleted ? 0 : Math.max(0, estimatedHours - taskTimeSpentHours);
    
    // Temps passé par ce membre cette semaine (pour affichage séparé)
    const memberTimeSpentMinutes = timeEntryThisWeekMap.get(task.id) || 0;
    const memberTimeSpentHours = memberTimeSpentMinutes / 60;

    // V2.4: PERF - Use cached assignee count from aggregated query (avoid N+1)
    const numAssignees = taskAssigneeCountMap.get(task.id) || 1;
    const manualAllocation = allocationMap.get(task.id);
    const hasManualAllocation = manualAllocation !== undefined && manualAllocation > 0;

    // V3: PERF - Use pre-fetched task allocations instead of querying in loop
    const totalTaskAllocated = taskTotalAllocationsMap.get(task.id) || 0;
    const unallocatedHours = taskRemainingHours - totalTaskAllocated;

    // V2: Calcul de l'imputation
    let imputedHours = 0;
    if (!isCompleted) {
      if (numAssignees === 1) {
        // Solo task: always keep displayed/imputed workload aligned with real remaining work.
        // If an older allocation exists (ex: estimation reduced from 22h to 18h), cap it.
        const soloCappedAllocation = hasManualAllocation
          ? Math.min(manualAllocation!, taskRemainingHours)
          : taskRemainingHours;
        imputedHours = soloCappedAllocation;
        
        // V8: Auto-create allocation in DB if not exists for solo tasks
        // V3.7: Skip auto-allocation when in readOnly mode (user has no management rights)
        if (!hasManualAllocation && taskRemainingHours > 0 && !readOnly) {
          // Fire and forget - create allocation asynchronously
          supabase
            .from('project_task_workload_allocations')
            .upsert({
              org_id: (at as any).org_id,
              task_id: task.id,
              org_member_id: memberId,
              week_start: weekStart,
              allocated_hours: taskRemainingHours,
            }, {
              onConflict: 'task_id,org_member_id,week_start',
            })
            .then(({ error }) => {
              if (error) {
                console.error('[V8] Error auto-allocating solo task:', error);
              } else {
                console.log(`[V8] Auto-allocated ${taskRemainingHours}h to solo task ${task.id}`);
              }
            });
        }
      } else if (hasManualAllocation) {
        // CAS 2: Multi-assignés avec allocation manuelle
        imputedHours = manualAllocation!;
        
        // V2.5: Check if task is PARTIALLY allocated - still needs distribution
        if (unallocatedHours > WORKLOAD_ALLOCATION_TOLERANCE_HOURS) {
          tasksNeedingAllocation.push({
            taskId: task.id,
            taskTitle: task.title,
            projectId: project.id,
            projectName: project.name,
            remainingHours: unallocatedHours, // Only the unallocated portion
            assigneeCount: numAssignees,
            dueAt: task.due_at,
          });
        }
      } else {
        // CAS 3: Multi-assignés SANS allocation pour CE membre
        // V3.4 FIX: Check if task is FULLY allocated to OTHER members before marking as "needs allocation"
        // If unallocatedHours <= tolerance, the task is fully distributed even if this member has no share
        imputedHours = 0;
        if (unallocatedHours > WORKLOAD_ALLOCATION_TOLERANCE_HOURS) {
          tasksNeedingAllocation.push({
            taskId: task.id,
            taskTitle: task.title,
            projectId: project.id,
            projectName: project.name,
            remainingHours: unallocatedHours, // V3.4: Show only unallocated portion, not total
            assigneeCount: numAssignees,
            dueAt: task.due_at,
          });
        }
      }
    }

    totalImputed += imputedHours;
    totalTimeSpent += memberTimeSpentHours;
    if (!isCompleted) {
      totalRemaining += taskRemainingHours;
    }

    // Aggregate by project (using imputed hours for allocation)
    const existing = projectMap.get(project.id);
    if (existing) {
      existing.allocatedHours += imputedHours;
      existing.timeSpentHours += memberTimeSpentHours;
      existing.remainingHours += taskRemainingHours;
      existing.taskCount += 1;
    } else {
      projectMap.set(project.id, {
        projectId: project.id,
        projectName: project.name,
        allocatedHours: imputedHours,
        timeSpentHours: memberTimeSpentHours,
        remainingHours: taskRemainingHours,
        taskCount: 1,
      });
    }

    // Task details with V2 info
    taskDetails.push({
      taskId: task.id,
      taskTitle: task.title,
      projectId: project.id,
      projectName: project.name,
      estimatedHours,
      timeSpentHours: taskTimeSpentHours,
      remainingHours: taskRemainingHours,
      dueAt: task.due_at,
      statusCode: task.status_code,
      assigneeCount: numAssignees,
      imputedHours,
      hasManualAllocation,
      isCompleted,
      totalAllocatedHours: totalTaskAllocated, // V3.3: Total allocated across all assignees
    });
  }

  // V6: L'utilisation GLOBALE inclut les heures externes pour cohérence inter-projets
  // totalImputed = heures du projet courant (si filtré) ou de tous les projets
  // Pour le calcul global, on ajoute les heures externes si on est en contexte projet
  const totalGlobalAllocated = totalImputed + externalAllocatedHours;
  const utilizationPercent = capacityHours > 0 
    ? Math.round((totalGlobalAllocated / capacityHours) * 100) 
    : 0;

  return {
    memberId: member.id,
    userId: member.user_id,
    fullName: profile.full_name,
    avatarUrl: profile.avatar_url,
    email: profile.email,
    capacityHours,
    allocatedHours: totalGlobalAllocated, // V6: Charge globale (projet courant + externe)
    timeSpentHours: totalTimeSpent,
    remainingWorkHours: totalRemaining, // Total restant sur les tâches (informationnel)
    availableHours: capacityHours - totalGlobalAllocated, // V6: Disponibilité basée sur charge globale
    utilizationPercent,
    projectBreakdown: Array.from(projectMap.values()),
    taskDetails: taskDetails.sort((a, b) => {
      // Sort by due date, then by remaining hours
      if (a.dueAt && b.dueAt) return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
      if (a.dueAt) return -1;
      if (b.dueAt) return 1;
      return b.remainingHours - a.remainingHours;
    }),
    tasksNeedingAllocation,
    externalAllocatedHours, // V6: Maintenant calculé si currentProjectId fourni
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// V3: FETCH TEAM WORKLOAD FOR A PROJECT - OPTIMIZED BATCH VERSION
// Reduces ~50 queries to ~8 queries by pre-fetching all data in batch
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchTeamWorkload(
  projectId: string,
  orgId: string,
  weekStart: string
): Promise<MemberWorkload[]> {
  const weekEnd = getWeekEnd(weekStart);
  
  // Step 1: Get project members with capacity
  const members = await fetchProjectMembersCapacity(projectId, orgId, weekStart);
  if (members.length === 0) return [];
  
  const memberIds = members.map(m => m.memberId);
  const userIds = members.map(m => m.userId);

  // Step 2: BATCH - Get task assignments for THIS PROJECT ONLY (security fix)
  // V4: Filter by project_id to prevent cross-project data leak
  const { data: allAssignments } = await supabase
    .from('project_task_assignees')
    .select(`
      org_member_id,
      task_id,
      project_tasks!inner (
        id,
        title,
        project_id,
        estimated_hours,
        due_at,
        status_code,
        created_at,
        project_projects!inner (
          id,
          name
        )
      )
    `)
    .in('org_member_id', memberIds)
    .eq('org_id', orgId)
    .eq('project_tasks.project_id', projectId); // V4: CRITICAL - Filter by current project

  // Get unique task IDs (now only from current project)
  const allTaskIds = [...new Set((allAssignments || []).map((a: any) => a.project_tasks.id))];
  
  // Get terminal status codes EARLY (needed for external calculation)
  const { data: terminalStatuses } = await supabase
    .from('project_task_statuses')
    .select('code')
    .eq('org_id', orgId)
    .eq('is_terminal', true);

  const terminalCodes = new Set((terminalStatuses || []).map(s => s.code));
  
  // Step 2b: V7 - Get EXTERNAL charge (from OTHER projects) for capacity calculation
  // This includes DB allocations + auto-imputed solo tasks (consistent with fetchMemberWorkload)
  
  // 2b.1: Get DB allocations from other projects
  // V8: Include status_code to filter out completed tasks
  const { data: externalAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select(`
      org_member_id,
      allocated_hours,
      task_id,
      project_tasks!inner (
        project_id,
        status_code
      )
    `)
    .in('org_member_id', memberIds)
    .eq('week_start', weekStart)
    .neq('project_tasks.project_id', projectId);
  
  // Track which tasks already have DB allocations per member
  // V8: Skip allocations for completed tasks
  const memberExternalAllocatedTasks = new Map<string, Set<string>>();
  const externalDbHoursMap = new Map<string, number>();
  (externalAllocations || []).forEach((ea: any) => {
    // V8: Filter out completed tasks from external charge calculation
    const taskStatus = ea.project_tasks?.status_code;
    if (taskStatus && terminalCodes.has(taskStatus)) {
      return; // Skip completed tasks
    }
    
    const current = externalDbHoursMap.get(ea.org_member_id) || 0;
    externalDbHoursMap.set(ea.org_member_id, current + (ea.allocated_hours || 0));
    
    if (!memberExternalAllocatedTasks.has(ea.org_member_id)) {
      memberExternalAllocatedTasks.set(ea.org_member_id, new Set());
    }
    memberExternalAllocatedTasks.get(ea.org_member_id)!.add(ea.task_id);
  });
  
  // 2b.2: Get solo tasks from other projects for auto-imputation
  const { data: externalSoloTasks } = await supabase
    .from('project_task_assignees')
    .select(`
      org_member_id,
      task_id,
      project_tasks!inner (
        id,
        project_id,
        estimated_hours,
        status_code
      )
    `)
    .in('org_member_id', memberIds)
    .neq('project_tasks.project_id', projectId);
  
  // Get external task IDs for time entries and assignee counts
  const externalTaskIds = [...new Set((externalSoloTasks || []).map((t: any) => t.task_id))];
  
  const { data: externalTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .in('task_id', externalTaskIds.length > 0 ? externalTaskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const externalTimeMap = new Map<string, number>();
  (externalTimeEntries || []).forEach(te => {
    externalTimeMap.set(te.task_id, (externalTimeMap.get(te.task_id) || 0) + te.duration_minutes);
  });
  
  const { data: externalAssigneeCounts } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', externalTaskIds.length > 0 ? externalTaskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const externalAssigneeCountMap = new Map<string, number>();
  (externalAssigneeCounts || []).forEach(a => {
    externalAssigneeCountMap.set(a.task_id, (externalAssigneeCountMap.get(a.task_id) || 0) + 1);
  });
  
  // Calculate external solo auto-imputed hours per member
  const externalSoloHoursMap = new Map<string, number>();
  for (const task of (externalSoloTasks || [])) {
    const t = task.project_tasks as any;
    if (terminalCodes.has(t.status_code)) continue;
    
    // Skip if already has DB allocation
    const memberAllocatedTasks = memberExternalAllocatedTasks.get(task.org_member_id);
    if (memberAllocatedTasks?.has(task.task_id)) continue;
    
    const assigneeCount = externalAssigneeCountMap.get(task.task_id) || 1;
    if (assigneeCount === 1) {
      const timeSpent = (externalTimeMap.get(task.task_id) || 0) / 60;
      const remaining = Math.max(0, (t.estimated_hours || 0) - timeSpent);
      const current = externalSoloHoursMap.get(task.org_member_id) || 0;
      externalSoloHoursMap.set(task.org_member_id, current + remaining);
    }
  }
  
  // Combine DB allocations + solo auto-imputed for final external hours
  const externalHoursMap = new Map<string, number>();
  for (const memberId of memberIds) {
    const dbHours = externalDbHoursMap.get(memberId) || 0;
    const soloHours = externalSoloHoursMap.get(memberId) || 0;
    externalHoursMap.set(memberId, dbHours + soloHours);
  }
  
  // Step 3: BATCH - Get ALL time entries for current project tasks in ONE query
  const { data: allTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, user_id, duration_minutes, logged_at')
    .in('task_id', allTaskIds.length > 0 ? allTaskIds : ['00000000-0000-0000-0000-000000000000']);

  // Step 4: BATCH - Get ALL workload allocations for current project tasks in ONE query
  const { data: allAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id, org_member_id, allocated_hours')
    .in('task_id', allTaskIds.length > 0 ? allTaskIds : ['00000000-0000-0000-0000-000000000000'])
    .eq('week_start', weekStart);

  // Step 5: BATCH - Get ALL assignee counts per task in ONE query
  const { data: allAssigneeCounts } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', allTaskIds.length > 0 ? allTaskIds : ['00000000-0000-0000-0000-000000000000']);

  // Pre-compute aggregations (terminalCodes already fetched above)
  // Total time spent per task (from ALL time entries)
  const taskTimeSpentMap = new Map<string, number>();
  (allTimeEntries || []).forEach(te => {
    taskTimeSpentMap.set(te.task_id, (taskTimeSpentMap.get(te.task_id) || 0) + te.duration_minutes);
  });

  // Time entries this week per user per task
  const userTaskTimeThisWeekMap = new Map<string, number>();
  (allTimeEntries || []).forEach(te => {
    const loggedAt = te.logged_at;
    if (loggedAt >= weekStart && loggedAt <= weekEnd + 'T23:59:59Z') {
      const key = `${te.user_id}:${te.task_id}`;
      userTaskTimeThisWeekMap.set(key, (userTaskTimeThisWeekMap.get(key) || 0) + te.duration_minutes);
    }
  });

  // Tasks with time entries this week per user
  const userTasksWithTimeThisWeek = new Map<string, Set<string>>();
  (allTimeEntries || []).forEach(te => {
    const loggedAt = te.logged_at;
    if (loggedAt >= weekStart && loggedAt <= weekEnd + 'T23:59:59Z') {
      if (!userTasksWithTimeThisWeek.has(te.user_id)) {
        userTasksWithTimeThisWeek.set(te.user_id, new Set());
      }
      userTasksWithTimeThisWeek.get(te.user_id)!.add(te.task_id);
    }
  });

  // Assignee count per task
  const taskAssigneeCountMap = new Map<string, number>();
  (allAssigneeCounts || []).forEach(ac => {
    taskAssigneeCountMap.set(ac.task_id, (taskAssigneeCountMap.get(ac.task_id) || 0) + 1);
  });

  // Allocations per member per task
  const memberTaskAllocationMap = new Map<string, number>();
  (allAllocations || []).forEach(a => {
    const key = `${a.org_member_id}:${a.task_id}`;
    memberTaskAllocationMap.set(key, a.allocated_hours);
  });

  // Total allocations per task (all assignees)
  const taskTotalAllocationsMap = new Map<string, number>();
  (allAllocations || []).forEach(a => {
    taskTotalAllocationsMap.set(a.task_id, (taskTotalAllocationsMap.get(a.task_id) || 0) + a.allocated_hours);
  });

  // Tasks with allocations this week per member
  const memberTasksWithAllocation = new Map<string, Set<string>>();
  (allAllocations || []).forEach(a => {
    if (!memberTasksWithAllocation.has(a.org_member_id)) {
      memberTasksWithAllocation.set(a.org_member_id, new Set());
    }
    memberTasksWithAllocation.get(a.org_member_id)!.add(a.task_id);
  });

  // Group assignments by member
  const memberAssignmentsMap = new Map<string, any[]>();
  (allAssignments || []).forEach((a: any) => {
    if (!memberAssignmentsMap.has(a.org_member_id)) {
      memberAssignmentsMap.set(a.org_member_id, []);
    }
    memberAssignmentsMap.get(a.org_member_id)!.push(a);
  });

  // Now process each member using pre-fetched data (no additional queries!)
  const workloads: MemberWorkload[] = [];

  for (const member of members) {
    const assignments = memberAssignmentsMap.get(member.memberId) || [];
    const tasksWithAllocationThisWeek = memberTasksWithAllocation.get(member.memberId) || new Set();
    const tasksWithTimeEntriesThisWeek = userTasksWithTimeThisWeek.get(member.userId) || new Set();
    // V4: Get external hours for this member (from other projects)
    const externalHours = externalHoursMap.get(member.memberId) || 0;

    // Filter tasks relevant to the week
    const filteredTasks = assignments.filter((at: any) => {
      const task = at.project_tasks;
      
      if (tasksWithAllocationThisWeek.has(task.id)) return true;
      if (tasksWithTimeEntriesThisWeek.has(task.id)) return true;
      
      const dueAt = task.due_at;
      const weekStartDate = new Date(weekStart);
      const weekEndDate = new Date(weekEnd + 'T23:59:59Z');
      
      if (dueAt) {
        const dueDate = new Date(dueAt);
        return dueDate >= weekStartDate;
      }
      
      const createdAt = new Date(task.created_at);
      return createdAt <= weekEndDate;
    });

    let totalImputed = 0;
    let totalTimeSpent = 0;
    let totalRemaining = 0;
    const projectMap = new Map<string, ProjectWorkloadBreakdown>();
    const taskDetails: TaskWorkloadDetail[] = [];
    const tasksNeedingAllocation: TaskNeedingAllocation[] = [];

    for (const at of filteredTasks) {
      const task = at.project_tasks as any;
      const project = task.project_projects;
      
      const isCompleted = terminalCodes.has(task.status_code);
      const estimatedHours = task.estimated_hours || 0;
      
      const totalTaskTimeSpentMinutes = taskTimeSpentMap.get(task.id) || 0;
      const taskTimeSpentHours = totalTaskTimeSpentMinutes / 60;
      const taskRemainingHours = isCompleted ? 0 : Math.max(0, estimatedHours - taskTimeSpentHours);
      
      const memberTimeKey = `${member.userId}:${task.id}`;
      const memberTimeSpentMinutes = userTaskTimeThisWeekMap.get(memberTimeKey) || 0;
      const memberTimeSpentHours = memberTimeSpentMinutes / 60;

      const numAssignees = taskAssigneeCountMap.get(task.id) || 1;
      const allocationKey = `${member.memberId}:${task.id}`;
      const manualAllocation = memberTaskAllocationMap.get(allocationKey);
      const hasManualAllocation = manualAllocation !== undefined && manualAllocation > 0;
      
      const totalTaskAllocated = taskTotalAllocationsMap.get(task.id) || 0;
      const unallocatedHours = taskRemainingHours - totalTaskAllocated;

      // V5: Calcul des heures imputées - TOUJOURS utiliser l'allocation DB si elle existe
      // Cela garantit la cohérence avec le calcul inter-projets (qui lit project_task_workload_allocations)
      let imputedHours = 0;
      if (!isCompleted) {
        if (numAssignees === 1) {
          // Solo task: keep allocation coherent with remaining work even if DB row is stale.
          const soloCappedAllocation = hasManualAllocation
            ? Math.min(manualAllocation!, taskRemainingHours)
            : taskRemainingHours;
          imputedHours = soloCappedAllocation;
        } else if (hasManualAllocation) {
          // CAS 2: Multi-assignés avec allocation manuelle
          imputedHours = manualAllocation!;
          
          // V2.5: Check if task is PARTIALLY allocated - still needs distribution
          if (unallocatedHours > WORKLOAD_ALLOCATION_TOLERANCE_HOURS) {
            tasksNeedingAllocation.push({
              taskId: task.id,
              taskTitle: task.title,
              projectId: project.id,
              projectName: project.name,
              remainingHours: unallocatedHours,
              assigneeCount: numAssignees,
              dueAt: task.due_at,
            });
          }
        } else {
          // CAS 3: Multi-assignés SANS allocation pour CE membre
          imputedHours = 0;
          if (unallocatedHours > WORKLOAD_ALLOCATION_TOLERANCE_HOURS) {
            tasksNeedingAllocation.push({
              taskId: task.id,
              taskTitle: task.title,
              projectId: project.id,
              projectName: project.name,
              remainingHours: unallocatedHours,
              assigneeCount: numAssignees,
              dueAt: task.due_at,
            });
          }
        }
      }

      totalImputed += imputedHours;
      totalTimeSpent += memberTimeSpentHours;
      if (!isCompleted) {
        totalRemaining += taskRemainingHours;
      }

      const existing = projectMap.get(project.id);
      if (existing) {
        existing.allocatedHours += imputedHours;
        existing.timeSpentHours += memberTimeSpentHours;
        existing.remainingHours += taskRemainingHours;
        existing.taskCount += 1;
      } else {
        projectMap.set(project.id, {
          projectId: project.id,
          projectName: project.name,
          allocatedHours: imputedHours,
          timeSpentHours: memberTimeSpentHours,
          remainingHours: taskRemainingHours,
          taskCount: 1,
        });
      }

      taskDetails.push({
        taskId: task.id,
        taskTitle: task.title,
        projectId: project.id,
        projectName: project.name,
        estimatedHours,
        timeSpentHours: taskTimeSpentHours,
        remainingHours: taskRemainingHours,
        dueAt: task.due_at,
        statusCode: task.status_code,
        assigneeCount: numAssignees,
        imputedHours,
        hasManualAllocation,
        isCompleted,
        totalAllocatedHours: totalTaskAllocated, // V3.3: Total allocated across all assignees
      });
    }

    // V4: Calculate utilization INCLUDING external hours for accurate capacity view
    const totalAllocatedIncludingExternal = totalImputed + externalHours;
    const utilizationPercent = member.effectiveCapacity > 0 
      ? Math.round((totalAllocatedIncludingExternal / member.effectiveCapacity) * 100) 
      : 0;

    workloads.push({
      memberId: member.memberId,
      userId: member.userId,
      fullName: member.fullName,
      avatarUrl: member.avatarUrl,
      email: member.email,
      capacityHours: member.effectiveCapacity,
      allocatedHours: totalImputed, // Only THIS project's hours
      timeSpentHours: totalTimeSpent,
      remainingWorkHours: totalRemaining,
      availableHours: member.effectiveCapacity - totalAllocatedIncludingExternal, // V4: Deduct external hours from availability
      utilizationPercent, // V4: Includes external hours
      projectBreakdown: Array.from(projectMap.values()),
      taskDetails: taskDetails.sort((a, b) => {
        if (a.dueAt && b.dueAt) return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
        if (a.dueAt) return -1;
        if (b.dueAt) return 1;
        return b.remainingHours - a.remainingHours;
      }),
      tasksNeedingAllocation, // V4: Only tasks from THIS project
      externalAllocatedHours: externalHours, // V4: Hours allocated on other projects
    });
  }

  return workloads;
}

// ─────────────────────────────────────────────────────────────────────────────
// V2: CALCULATE TEAM SUMMARY WITH TASKS NEEDING ALLOCATION COUNT
// ─────────────────────────────────────────────────────────────────────────────

export function calculateTeamSummary(workloads: MemberWorkload[]): TeamWorkloadSummary {
  if (workloads.length === 0) {
    return {
      totalCapacityHours: 0,
      totalAllocatedHours: 0,
      totalRemainingWorkHours: 0,
      overloadedMembers: 0,
      underutilizedMembers: 0,
      optimalMembers: 0,
      averageUtilization: 0,
      tasksNeedingAllocationCount: 0,
      hoursNeedingAllocation: 0,
    };
  }

  let totalCapacity = 0;
  let totalAllocated = 0;
  let totalRemaining = 0;
  let overloaded = 0;
  let underutilized = 0;
  let optimal = 0;

  // V2.2: Collect unique tasks needing allocation with their hours
  const tasksNeedingAllocationMap = new Map<string, number>();

  workloads.forEach(w => {
    totalCapacity += w.capacityHours;
    totalAllocated += w.allocatedHours;
    totalRemaining += w.remainingWorkHours;

    const status = getUtilizationStatus(w.utilizationPercent);
    if (status === 'overloaded') overloaded++;
    else if (status === 'low') underutilized++;
    else optimal++;

    // V2.2: Collect unique tasks needing allocation with their remaining hours
    w.tasksNeedingAllocation.forEach(t => {
      // Only store the task once (first occurrence keeps the hours)
      if (!tasksNeedingAllocationMap.has(t.taskId)) {
        tasksNeedingAllocationMap.set(t.taskId, t.remainingHours);
      }
    });
  });

  // V2.2: Sum up total hours needing allocation
  let hoursNeedingAllocation = 0;
  tasksNeedingAllocationMap.forEach(hours => {
    hoursNeedingAllocation += hours;
  });

  return {
    totalCapacityHours: totalCapacity,
    totalAllocatedHours: totalAllocated,
    totalRemainingWorkHours: totalRemaining,
    overloadedMembers: overloaded,
    underutilizedMembers: underutilized,
    optimalMembers: optimal,
    averageUtilization: totalCapacity > 0 ? Math.round((totalAllocated / totalCapacity) * 100) : 0,
    tasksNeedingAllocationCount: tasksNeedingAllocationMap.size,
    hoursNeedingAllocation,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE MEMBER CAPACITY (DEFAULT)
// Also clears all weekly overrides so the new default applies everywhere
// ─────────────────────────────────────────────────────────────────────────────

export async function updateMemberCapacity(
  memberId: string,
  weeklyCapacityHours: number
): Promise<boolean> {
  // Step 1: Update the default capacity
  const { data, error } = await supabase
    .from('core_organization_members')
    .update({ weekly_capacity_hours: weeklyCapacityHours })
    .eq('id', memberId)
    .select('id, org_id');

  if (error) {
    console.error('Error updating member capacity:', error);
    return false;
  }
  
  // If no rows were returned, the update didn't apply (likely RLS policy blocked it)
  if (!data || data.length === 0) {
    console.error('No rows updated - check RLS policy or permissions');
    return false;
  }

  // Step 2: Delete ALL weekly overrides for this member
  // This ensures the new default applies to all weeks (past, current, future)
  const { error: deleteError } = await supabase
    .from('core_member_capacity_overrides')
    .delete()
    .eq('member_id', memberId);

  if (deleteError) {
    console.error('Error deleting capacity overrides:', deleteError);
    // Note: We don't return false here as the main update succeeded
    // The overrides will just remain but new default is still set
  }
  
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// SET CAPACITY OVERRIDE FOR A WEEK
// ─────────────────────────────────────────────────────────────────────────────

export async function setCapacityOverride(
  orgId: string,
  memberId: string,
  weekStart: string,
  overrideHours: number,
  reason?: string,
  createdBy?: string
): Promise<boolean> {
  const { error } = await supabase
    .from('core_member_capacity_overrides')
    .upsert({
      org_id: orgId,
      member_id: memberId,
      week_start: weekStart,
      override_hours: overrideHours,
      reason: reason || null,
      created_by: createdBy || null,
    }, {
      onConflict: 'member_id,week_start',
    });

  if (error) {
    console.error('Error setting capacity override:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPSERT WORKLOAD ALLOCATION
// ─────────────────────────────────────────────────────────────────────────────

export async function upsertWorkloadAllocation(
  orgId: string,
  taskId: string,
  memberId: string,
  weekStart: string,
  allocatedHours: number
): Promise<boolean> {
  const { error } = await supabase
    .from('project_task_workload_allocations')
    .upsert({
      org_id: orgId,
      task_id: taskId,
      org_member_id: memberId,
      week_start: weekStart,
      allocated_hours: allocatedHours,
    }, {
      onConflict: 'task_id,org_member_id,week_start',
    });

  if (error) {
    console.error('Error upserting workload allocation:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// BULK ALLOCATE TASK TO MEMBERS
// V2.5: Enregistre les allocations (prévisionnelles) pour chaque membre
// Ces allocations sont visibles dans le TaskDrawer et la feuille de temps
// ─────────────────────────────────────────────────────────────────────────────

export async function bulkAllocateTask(
  orgId: string,
  taskId: string,
  weekStart: string,
  allocations: Array<{ memberId: string; hours: number }>,
  projectId?: string,
  performedBy?: string
): Promise<boolean> {
  // V3.2: Upsert new allocations FIRST, then delete obsolete ones
  // This prevents race conditions while ensuring old allocations are cleaned up
  const memberIdsWithAllocation = new Set(allocations.filter(a => a.hours > 0).map(a => a.memberId));
  
  const records = allocations
    .filter(a => a.hours > 0)
    .map(a => ({
      org_id: orgId,
      task_id: taskId,
      org_member_id: a.memberId,
      week_start: weekStart,
      allocated_hours: a.hours,
    }));

  // Step 1: Upsert new/updated allocations first (so data is never empty)
  if (records.length > 0) {
    const { error } = await supabase
      .from('project_task_workload_allocations')
      .upsert(records, {
        onConflict: 'task_id,org_member_id,week_start',
      });

    if (error) {
      console.error('Error bulk allocating task:', error);
      return false;
    }
  }

  // Step 2: Delete ALL allocations for this task/week that are NOT in the new list
  // This handles both explicit hours=0 AND old assignees no longer in the allocation list
  if (memberIdsWithAllocation.size > 0) {
    // Delete allocations for members NOT in the new list
    const { error: deleteError } = await supabase
      .from('project_task_workload_allocations')
      .delete()
      .eq('task_id', taskId)
      .eq('week_start', weekStart)
      .not('org_member_id', 'in', `(${Array.from(memberIdsWithAllocation).join(',')})`);
    
    if (deleteError) {
      console.error('Error cleaning obsolete allocations:', deleteError);
    }
  } else {
    // If no new allocations, delete all for this task/week
    await supabase
      .from('project_task_workload_allocations')
      .delete()
      .eq('task_id', taskId)
      .eq('week_start', weekStart);
  }

  // V3: Create audit trail entry if projectId and performedBy are provided
  if (projectId && performedBy) {
    try {
      // Get task title and member names for the audit
      const { data: task } = await supabase
        .from('project_tasks')
        .select('title')
        .eq('id', taskId)
        .single();

      const memberIds = allocations.filter(a => a.hours > 0).map(a => a.memberId);
      const { data: members } = await supabase
        .from('core_organization_members')
        .select('id, core_profiles(full_name)')
        .in('id', memberIds);

      const memberNameMap = new Map(
        (members || []).map((m: any) => [m.id, m.core_profiles?.full_name || 'Membre'])
      );

      const auditAllocations = allocations
        .filter(a => a.hours > 0)
        .map(a => ({
          memberId: a.memberId,
          memberName: memberNameMap.get(a.memberId) || 'Membre',
          hours: a.hours,
        }));

      const totalHours = allocations.reduce((sum, a) => sum + a.hours, 0);

      await supabase
        .from('project_workload_allocation_audit')
        .insert({
          org_id: orgId,
          project_id: projectId,
          task_id: taskId,
          action: 'create',
          performed_by: performedBy,
          week_start: weekStart,
          allocations: auditAllocations,
          total_hours: totalHours,
          task_title: task?.title || null,
        });
    } catch (auditError) {
      // Don't fail the main operation if audit fails
      console.warn('Failed to create allocation audit entry:', auditError);
    }
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// V2.5: GET USER ALLOCATION FOR A TASK
// Returns the allocated hours for a specific user on a task for current week
// ─────────────────────────────────────────────────────────────────────────────

export async function getUserTaskAllocation(
  taskId: string,
  memberId: string,
  weekStart?: string
): Promise<number> {
  const targetWeek = weekStart || getWeekStart();
  
  const { data, error } = await supabase
    .from('project_task_workload_allocations')
    .select('allocated_hours')
    .eq('task_id', taskId)
    .eq('org_member_id', memberId)
    .eq('week_start', targetWeek)
    .maybeSingle();

  if (error || !data) return 0;
  return data.allocated_hours || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// V2.5: GET ALL ALLOCATIONS FOR A TASK
// Returns all member allocations for a task for current week
// ─────────────────────────────────────────────────────────────────────────────

export async function getTaskAllocations(
  taskId: string,
  weekStart?: string
): Promise<Array<{ memberId: string; userId: string; allocatedHours: number; fullName: string | null }>> {
  const targetWeek = weekStart || getWeekStart();

  const [{ data, error }, { data: taskMeta }, { data: timeEntries }, { count: assigneeCount }] = await Promise.all([
    supabase
      .from('project_task_workload_allocations')
      .select(`
        allocated_hours,
        org_member_id,
        core_organization_members!inner (
          id,
          user_id,
          core_profiles!inner (
            full_name
          )
        )
      `)
      .eq('task_id', taskId)
      .eq('week_start', targetWeek),
    supabase
      .from('project_tasks')
      .select('estimated_hours')
      .eq('id', taskId)
      .maybeSingle(),
    supabase
      .from('project_task_time_entries')
      .select('duration_minutes')
      .eq('task_id', taskId),
    supabase
      .from('project_task_assignees')
      .select('org_member_id', { count: 'exact', head: true })
      .eq('task_id', taskId),
  ]);

  if (error || !data) return [];

  const isSoloTask = (assigneeCount ?? 0) === 1;
  const timeSpentMinutes = (timeEntries || []).reduce((sum, te) => sum + te.duration_minutes, 0);
  const remainingHours = Math.max(0, (taskMeta?.estimated_hours || 0) - (timeSpentMinutes / 60));

  return data.map((row: any) => ({
    memberId: row.org_member_id,
    userId: row.core_organization_members.user_id,
    allocatedHours: isSoloTask ? Math.min(row.allocated_hours, remainingHours) : row.allocated_hours,
    fullName: row.core_organization_members.core_profiles.full_name,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// V2: GET TASK ALLOCATION STATUS WITH "ADD COLLABORATOR" INFO
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskAllocationStatus {
  taskId: string;
  taskTitle: string;
  projectId: string;
  estimatedHours: number;
  timeSpentHours: number;
  remainingHours: number;
  assignees: Array<{
    memberId: string;
    userId: string;
    fullName: string | null;
    avatarUrl: string | null;
    allocatedHours: number;
    availableCapacity: number;
    canAbsorb: boolean;
  }>;
  totalAllocated: number;
  remainingToAllocate: number;
  canBeAbsorbed: boolean;
  // V2: Indicateur de surcharge inévitable
  overloadUnavoidable: boolean;
  // V2: Membres disponibles du projet (non assignés à cette tâche)
  availableMembers: Array<{
    memberId: string;
    userId: string;
    fullName: string | null;
    avatarUrl: string | null;
    availableCapacity: number;
  }>;
}

export async function getTaskAllocationStatus(
  taskId: string,
  orgId: string,
  weekStart: string
): Promise<TaskAllocationStatus | null> {
  // Get task details with project
  const { data: task, error: taskError } = await supabase
    .from('project_tasks')
    .select('id, title, estimated_hours, project_id')
    .eq('id', taskId)
    .single();

  if (taskError || !task) {
    console.error('Error fetching task:', taskError);
    return null;
  }

  // V2.3: Get time spent from time_entries (SINGLE SOURCE OF TRUTH)
  const { data: taskTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('duration_minutes')
    .eq('task_id', taskId);
  
  const totalTimeSpentMinutes = (taskTimeEntries || []).reduce((sum, te) => sum + te.duration_minutes, 0);

  // Get assignees
  const { data: assignees, error: assigneesError } = await supabase
    .from('project_task_assignees')
    .select(`
      org_member_id,
      core_organization_members!inner (
        id,
        user_id,
        weekly_capacity_hours,
        core_profiles!inner (
          full_name,
          avatar_url
        )
      )
    `)
    .eq('task_id', taskId)
    .eq('org_id', orgId);

  if (assigneesError) {
    console.error('Error fetching assignees:', assigneesError);
    return null;
  }

  const estimatedHours = task.estimated_hours || 0;
  const timeSpentHours = totalTimeSpentMinutes / 60;
  const remainingHours = Math.max(0, estimatedHours - timeSpentHours);

  // Get existing allocations
  const assigneeMemberIds = (assignees || []).map((a: any) => a.core_organization_members.id);
  const { data: allocations } = await supabase
    .from('project_task_workload_allocations')
    .select('org_member_id, allocated_hours')
    .eq('task_id', taskId)
    .eq('week_start', weekStart)
    .in('org_member_id', assigneeMemberIds.length > 0 ? assigneeMemberIds : ['00000000-0000-0000-0000-000000000000']);

  let allocationMap = new Map((allocations || []).map(a => [a.org_member_id, a.allocated_hours]));

  // V8: Auto-allocate 100% for solo tasks that don't have an allocation yet
  const isSoloTask = assignees && assignees.length === 1;
  const hasNoAllocation = !allocations || allocations.length === 0 || allocations.every(a => a.allocated_hours === 0);
  
  let autoAllocated = false;
  if (isSoloTask && hasNoAllocation && remainingHours > 0) {
    const soloMemberId = assigneeMemberIds[0];
    
    // Create the allocation in DB
    const { error: upsertError } = await supabase
      .from('project_task_workload_allocations')
      .upsert({
        org_id: orgId,
        task_id: taskId,
        org_member_id: soloMemberId,
        week_start: weekStart,
        allocated_hours: remainingHours,
      }, {
        onConflict: 'task_id,org_member_id,week_start',
      });
    
    if (!upsertError) {
      // Update local map with auto-allocated value
      allocationMap = new Map([[soloMemberId, remainingHours]]);
      autoAllocated = true;
      console.log(`[V8] Auto-allocated ${remainingHours}h to solo assignee ${soloMemberId} for task ${taskId}`);
      
      // V9: Emit event for toast notification
      window.dispatchEvent(new CustomEvent('workload:solo-auto-allocated', {
        detail: {
          taskId,
          taskTitle: task.title,
          allocatedHours: remainingHours,
          memberId: soloMemberId,
        }
      }));
    }
  }

  // Get terminal statuses once (avoid repeated queries)
  const { data: terminalStatuses } = await supabase
    .from('project_task_statuses')
    .select('code')
    .eq('org_id', orgId)
    .eq('is_terminal', true);
  
  const terminalCodes = new Set((terminalStatuses || []).map(s => s.code));

  // V2.4: PERF - Pre-fetch data for all assignees in parallel to avoid N+1
  
  // Get ALL allocations for all members this week in ONE query
  const { data: allMembersAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('org_member_id, allocated_hours, task_id')
    .in('org_member_id', assigneeMemberIds.length > 0 ? assigneeMemberIds : ['00000000-0000-0000-0000-000000000000'])
    .eq('week_start', weekStart);
  
  // Group allocations by member
  const memberAllocationsMap = new Map<string, Array<{ allocated_hours: number; task_id: string }>>();
  (allMembersAllocations || []).forEach(a => {
    const list = memberAllocationsMap.get(a.org_member_id) || [];
    list.push({ allocated_hours: a.allocated_hours, task_id: a.task_id });
    memberAllocationsMap.set(a.org_member_id, list);
  });

  // Get ALL assigned tasks for all members in ONE query
  const { data: allMembersAssignedTasks } = await supabase
    .from('project_task_assignees')
    .select(`
      org_member_id,
      task_id,
      project_tasks!inner (
        id,
        estimated_hours,
        status_code
      )
    `)
    .in('org_member_id', assigneeMemberIds.length > 0 ? assigneeMemberIds : ['00000000-0000-0000-0000-000000000000'])
    .eq('org_id', orgId);
  
  // Group assigned tasks by member
  const memberAssignedTasksMap = new Map<string, Array<any>>();
  (allMembersAssignedTasks || []).forEach(at => {
    const list = memberAssignedTasksMap.get(at.org_member_id) || [];
    list.push(at);
    memberAssignedTasksMap.set(at.org_member_id, list);
  });

  // Get all task IDs for time entries and assignee counts
  const allMemberTaskIds = (allMembersAssignedTasks || []).map((at: any) => at.task_id);
  
  // Get ALL time entries for all member tasks in ONE query
  const { data: allMembersTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .in('task_id', allMemberTaskIds.length > 0 ? allMemberTaskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const allTasksTimeMap = new Map<string, number>();
  (allMembersTimeEntries || []).forEach(te => {
    allTasksTimeMap.set(te.task_id, (allTasksTimeMap.get(te.task_id) || 0) + te.duration_minutes);
  });

  // V2.4: Get ALL assignee counts in ONE query
  const { data: allTaskAssignees } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', allMemberTaskIds.length > 0 ? allMemberTaskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const taskAssigneeCountMap = new Map<string, number>();
  (allTaskAssignees || []).forEach(a => {
    taskAssigneeCountMap.set(a.task_id, (taskAssigneeCountMap.get(a.task_id) || 0) + 1);
  });

  // V2: Get FULL workload of each assignee (multi-project capacity) - now using pre-fetched data
  const assigneeDetails = (assignees || []).map((a: any) => {
    const member = a.core_organization_members;
    const profile = member.core_profiles;
    
    // Get this member's allocations from pre-fetched data
    const memberAllocations = memberAllocationsMap.get(member.id) || [];
    
    // Get this member's assigned tasks from pre-fetched data
    const memberAssignedTasks = memberAssignedTasksMap.get(member.id) || [];

    // Calculate imputed hours from single-assignee tasks
    let singleAssigneeImputed = 0;
    for (const sat of memberAssignedTasks) {
      const satTask = sat.project_tasks as any;
      if (terminalCodes.has(satTask.status_code)) continue;
      
      // V2.4: Use pre-fetched assignee count
      const satAssigneeCount = taskAssigneeCountMap.get(sat.task_id) || 1;
      
      if (satAssigneeCount === 1) {
        // V2.4: Use pre-fetched time_entries for remaining work
        const taskTimeSpentMinutes = allTasksTimeMap.get(sat.task_id) || 0;
        const taskRemaining = Math.max(0, (satTask.estimated_hours || 0) - taskTimeSpentMinutes / 60);
        singleAssigneeImputed += taskRemaining;
      }
    }

    // Calculate other allocated (exclude current task)
    const otherAllocated = memberAllocations
      .filter(al => al.task_id !== taskId)
      .reduce((sum, a) => sum + a.allocated_hours, 0);
    
    const capacity = member.weekly_capacity_hours ?? 35;
    // V2: Available capacity = capacity - other allocations - single-assignee imputed tasks
    const availableCapacity = Math.max(0, capacity - otherAllocated - singleAssigneeImputed);
    // Solo tasks: always show full remaining hours as allocated (auto-imputation)
    const currentAllocation = (isSoloTask && remainingHours > 0)
      ? remainingHours
      : (allocationMap.get(member.id) || 0);

    return {
      memberId: member.id,
      userId: member.user_id,
      fullName: profile.full_name,
      avatarUrl: profile.avatar_url,
      allocatedHours: currentAllocation,
      availableCapacity,
      canAbsorb: availableCapacity >= remainingHours / (assignees?.length || 1),
    };
  });

  const totalAllocated = assigneeDetails.reduce((sum, a) => sum + a.allocatedHours, 0);
  const remainingToAllocate = Math.max(0, remainingHours - totalAllocated);
  const totalAvailable = assigneeDetails.reduce((sum, a) => sum + a.availableCapacity, 0);
  
  // V2: Check if overload is unavoidable
  const overloadUnavoidable = totalAvailable < remainingToAllocate;

  // V2/6B: Get available project members who are NOT assigned to this task (using new RPC)
  const { data: allProjectMembersData } = await supabase.rpc('get_project_members', {
    _project_id: task.project_id,
  });

  // Get org member details with capacity for project members
  const projectMemberIds = (allProjectMembersData || []).map((m: any) => m.org_member_id);
  
  const { data: orgMembersData } = await supabase
    .from('core_organization_members')
    .select(`
      id,
      user_id,
      weekly_capacity_hours,
      core_profiles!inner (
        full_name,
        avatar_url
      )
    `)
    .in('id', projectMemberIds);

  const assignedMemberIds = new Set(assigneeMemberIds);
  const availableMembersRaw = (orgMembersData || []).filter((m: any) => 
    !assignedMemberIds.has(m.id)
  );

  // Calculate available capacity for each non-assigned member
  const availableMembers = await Promise.all(
    availableMembersRaw.map(async (member: any) => {
      const profile = member.core_profiles;
      
      // Get full workload for this member
      // V6: Pass task.project_id to correctly calculate cross-project hours
      const workload = await fetchMemberWorkload(member.id, orgId, weekStart, task.project_id);
      const availableCapacity = workload?.availableHours ?? (member.weekly_capacity_hours ?? 35);

      return {
        memberId: member.id,
        userId: member.user_id,
        fullName: profile.full_name,
        avatarUrl: profile.avatar_url,
        availableCapacity: Math.max(0, availableCapacity),
      };
    })
  );

  return {
    taskId: task.id,
    taskTitle: task.title,
    projectId: task.project_id,
    estimatedHours,
    timeSpentHours,
    remainingHours,
    assignees: assigneeDetails,
    totalAllocated,
    remainingToAllocate,
    canBeAbsorbed: totalAvailable >= remainingToAllocate,
    overloadUnavoidable,
    availableMembers: availableMembers.filter(m => m.availableCapacity > 0),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// V2: GET TASKS NEEDING ALLOCATION FOR A PROJECT
// ─────────────────────────────────────────────────────────────────────────────

export async function getTasksNeedingAllocation(
  projectId: string,
  orgId: string,
  weekStart: string
): Promise<TaskNeedingAllocation[]> {
  // Get all non-terminal tasks in project
  const { data: terminalStatuses } = await supabase
    .from('project_task_statuses')
    .select('code')
    .eq('org_id', orgId)
    .eq('is_terminal', true);

  const terminalCodes = (terminalStatuses || []).map(s => s.code);

  // Get tasks with more than 1 assignee that have no allocations
  const { data: tasks } = await supabase
    .from('project_tasks')
    .select(`
      id,
      title,
      estimated_hours,
      due_at,
      project_id,
      project_projects!inner (
        id,
        name
      )
    `)
    .eq('project_id', projectId)
    .eq('org_id', orgId)
    .not('status_code', 'in', `(${terminalCodes.length > 0 ? terminalCodes.join(',') : '__none__'})`);

  // V2.4: PERF - Get time entries for all tasks in ONE query (SINGLE SOURCE OF TRUTH)
  const taskIds = (tasks || []).map(t => t.id);
  const { data: allTimeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const taskTimeMap = new Map<string, number>();
  (allTimeEntries || []).forEach(te => {
    taskTimeMap.set(te.task_id, (taskTimeMap.get(te.task_id) || 0) + te.duration_minutes);
  });

  // V2.4: PERF - Get ALL assignee counts in ONE query (avoid N+1)
  const { data: allAssignees } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000']);
  
  const taskAssigneeCountMap = new Map<string, number>();
  (allAssignees || []).forEach(a => {
    taskAssigneeCountMap.set(a.task_id, (taskAssigneeCountMap.get(a.task_id) || 0) + 1);
  });

  // V2.4: PERF - Get ALL allocations for this week in ONE query (avoid N+1)
  // V3: Get allocated_hours to check if task is fully allocated
  const { data: allAllocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id, allocated_hours')
    .eq('week_start', weekStart)
    .in('task_id', taskIds.length > 0 ? taskIds : ['00000000-0000-0000-0000-000000000000']);
  
  // V3: Aggregate total allocations per task (sum of all assignees)
  const taskTotalAllocationsMap = new Map<string, number>();
  (allAllocations || []).forEach(a => {
    taskTotalAllocationsMap.set(a.task_id, (taskTotalAllocationsMap.get(a.task_id) || 0) + a.allocated_hours);
  });

  const tasksNeedingAllocation: TaskNeedingAllocation[] = [];

  for (const task of (tasks || [])) {
    // V2.4: Use cached assignee count
    const assigneeCount = taskAssigneeCountMap.get(task.id) || 0;
    if (assigneeCount <= 1) continue;

    // V2.4: Use time_entries for remaining hours (TOTAL, not weekly)
    const timeSpentMinutes = taskTimeMap.get(task.id) || 0;
    const remainingHours = Math.max(0, (task.estimated_hours || 0) - (timeSpentMinutes / 60));
    
    // V3: Check if task is fully allocated (total allocations >= remaining hours)
    const totalAllocated = taskTotalAllocationsMap.get(task.id) || 0;
    const unallocatedHours = remainingHours - totalAllocated;
    
    // Skip if fully allocated within the same tolerance used by the dialog UI
    if (unallocatedHours <= WORKLOAD_ALLOCATION_TOLERANCE_HOURS) continue;
    const project = task.project_projects as any;

    tasksNeedingAllocation.push({
      taskId: task.id,
      taskTitle: task.title,
      projectId: project.id,
      projectName: project.name,
      remainingHours: unallocatedHours, // V3: Only the unallocated portion
      assigneeCount,
      dueAt: task.due_at,
    });
  }

  return tasksNeedingAllocation;
}

// ─────────────────────────────────────────────────────────────────────────────
// V2: ADD ASSIGNEE TO TASK
// V8: Auto-allocate 100% for solo tasks
// ─────────────────────────────────────────────────────────────────────────────

export async function addAssigneeToTask(
  taskId: string,
  memberId: string,
  orgId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('project_task_assignees')
    .insert({
      task_id: taskId,
      org_member_id: memberId,
      org_id: orgId,
    });

  if (error) {
    console.error('Error adding assignee to task:', error);
    return false;
  }

  // V14: Check final assignee count to manage allocations correctly
  const { data: assigneesAfter } = await supabase
    .from('project_task_assignees')
    .select('org_member_id')
    .eq('task_id', taskId);

  if (assigneesAfter && assigneesAfter.length > 1) {
    // Solo → Multi: clear any prior solo auto-allocation so task reappears as "à répartir"
    const { error: delError } = await supabase
      .from('project_task_workload_allocations')
      .delete()
      .eq('task_id', taskId);
    if (delError) {
      console.error('Error clearing allocations on solo→multi transition:', delError);
    } else {
      console.log(`[V14] Task ${taskId} is now multi-assignee — cleared prior allocations`);
    }
  } else {
    // Still solo: keep auto-allocation logic in sync
    await autoAllocateSoloTask(taskId, orgId);
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// V8: AUTO-ALLOCATE SOLO TASK
// When a task has exactly 1 assignee, keep allocation synced with remaining work
// V13: Solo allocations are updated on estimation changes to avoid stale values
// ─────────────────────────────────────────────────────────────────────────────

export async function autoAllocateSoloTask(
  taskId: string,
  orgId: string
): Promise<boolean> {
  try {
    // Get current assignees count
    const { data: assignees, error: assigneesError } = await supabase
      .from('project_task_assignees')
      .select('org_member_id')
      .eq('task_id', taskId);

    if (assigneesError || !assignees) {
      console.error('Error fetching assignees for auto-allocation:', assigneesError);
      return false;
    }

    // Only auto-allocate for solo tasks
    if (assignees.length !== 1) {
      return false;
    }

    const soloMemberId = assignees[0].org_member_id;
    const weekStart = getWeekStart();

    // Pour une tâche solo, l'allocation doit toujours refléter l'estimation courante
    // (reste à faire), même si une allocation existait déjà.
    // Cela évite les incohérences du type estimation 17h mais allocation affichée 22h.

    // Get task info for remaining hours calculation
    const { data: task, error: taskError } = await supabase
      .from('project_tasks')
      .select('estimated_hours')
      .eq('id', taskId)
      .single();

    if (taskError || !task) {
      console.error('Error fetching task for auto-allocation:', taskError);
      return false;
    }

    // Get time spent to calculate remaining hours
    const { data: timeEntries } = await supabase
      .from('project_task_time_entries')
      .select('duration_minutes')
      .eq('task_id', taskId);

    const timeSpentMinutes = (timeEntries || []).reduce((sum, te) => sum + te.duration_minutes, 0);
    const remainingHours = Math.max(0, (task.estimated_hours || 0) - (timeSpentMinutes / 60));

    // Auto-allocate 100% to the solo assignee (insert or update to stay in sync)
    if (remainingHours > 0) {
      const { error: allocError } = await supabase
        .from('project_task_workload_allocations')
        .upsert({
          org_id: orgId,
          task_id: taskId,
          org_member_id: soloMemberId,
          week_start: weekStart,
          allocated_hours: remainingHours,
        }, {
          onConflict: 'task_id,org_member_id,week_start',
        });

      if (allocError) {
        console.error('Error auto-allocating solo task:', allocError);
        return false;
      }

      console.log(`[V13] Synced solo allocation to ${remainingHours}h for task ${taskId}`);
    }

    return true;
  } catch (err) {
    console.error('Error in autoAllocateSoloTask:', err);
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// V4: GET TASK ALLOCATION PERCENTAGES FOR MULTIPLE TASKS (BATCH)
// Pour afficher l'indicateur d'allocation sur les cartes Kanban
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskAllocationInfo {
  taskId: string;
  estimatedHours: number;
  allocatedHours: number;
  assigneeCount: number;
  isMultiAssignee: boolean;
  allocationPercent: number;
}

export async function fetchTasksAllocationInfo(
  taskIds: string[],
  orgId: string
): Promise<Map<string, TaskAllocationInfo>> {
  const result = new Map<string, TaskAllocationInfo>();
  
  if (taskIds.length === 0) return result;
  
  // 1. Get task basic info (estimated_hours)
  const { data: tasks } = await supabase
    .from('project_tasks')
    .select('id, estimated_hours')
    .in('id', taskIds);
  
  if (!tasks) return result;
  
  // 2. Get assignee counts for all tasks
  const { data: allAssignees } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .in('task_id', taskIds);
  
  const assigneeCountMap = new Map<string, number>();
  (allAssignees || []).forEach(a => {
    assigneeCountMap.set(a.task_id, (assigneeCountMap.get(a.task_id) || 0) + 1);
  });
  
  // 3. Get time spent for remaining hours calculation
  const { data: timeEntries } = await supabase
    .from('project_task_time_entries')
    .select('task_id, duration_minutes')
    .in('task_id', taskIds);
  
  const timeSpentMap = new Map<string, number>();
  (timeEntries || []).forEach(te => {
    timeSpentMap.set(te.task_id, (timeSpentMap.get(te.task_id) || 0) + te.duration_minutes);
  });
  
  // 4. Get allocations for all tasks (sum per task)
  const weekStart = getWeekStart();
  const { data: allocations } = await supabase
    .from('project_task_workload_allocations')
    .select('task_id, allocated_hours')
    .eq('week_start', weekStart)
    .in('task_id', taskIds);
  
  const allocationMap = new Map<string, number>();
  (allocations || []).forEach(a => {
    allocationMap.set(a.task_id, (allocationMap.get(a.task_id) || 0) + a.allocated_hours);
  });
  
  // 5. Build result for each task
  for (const task of tasks) {
    const assigneeCount = assigneeCountMap.get(task.id) || 0;
    const isMultiAssignee = assigneeCount > 1;
    const estimatedHours = task.estimated_hours || 0;
    const timeSpentMinutes = timeSpentMap.get(task.id) || 0;
    const remainingHours = Math.max(0, estimatedHours - (timeSpentMinutes / 60));
    const allocatedHours = allocationMap.get(task.id) || 0;
    
    // Pour mono-assigné, considérer comme 100% alloué (auto-imputé)
    const effectiveAllocated = isMultiAssignee ? allocatedHours : remainingHours;
    const allocationPercent = remainingHours > 0 
      ? Math.min(100, Math.round((effectiveAllocated / remainingHours) * 100))
      : 100;
    
    result.set(task.id, {
      taskId: task.id,
      estimatedHours: remainingHours, // Utiliser remaining comme base
      allocatedHours: isMultiAssignee ? allocatedHours : remainingHours,
      assigneeCount,
      isMultiAssignee,
      allocationPercent,
    });
  }
  
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// CAPACITY HISTORY FUNCTIONS (Moved from UI for architecture compliance)
// ─────────────────────────────────────────────────────────────────────────────

export interface CapacityOverride {
  id: string;
  weekStart: string;
  overrideHours: number;
  reason: string | null;
  createdAt: string;
  createdBy: string | null;
}

/**
 * Fetch capacity overrides history for a member
 */
export async function fetchMemberCapacityOverrides(memberId: string, limit = 10): Promise<CapacityOverride[]> {
  const { data, error } = await supabase
    .from('core_member_capacity_overrides')
    .select('id, week_start, override_hours, reason, created_at, created_by')
    .eq('member_id', memberId)
    .order('week_start', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error loading capacity overrides:', error);
    return [];
  }

  return (data || []).map(row => ({
    id: row.id,
    weekStart: row.week_start,
    overrideHours: row.override_hours,
    reason: row.reason,
    createdAt: row.created_at,
    createdBy: row.created_by,
  }));
}

/**
 * Delete a capacity override
 */
export async function deleteCapacityOverride(overrideId: string): Promise<boolean> {
  const { error } = await supabase
    .from('core_member_capacity_overrides')
    .delete()
    .eq('id', overrideId);

  if (error) {
    console.error('Error deleting capacity override:', error);
    return false;
  }

  return true;
}

/**
 * Fetch member's default capacity (without week override)
 */
export async function fetchMemberDefaultCapacity(memberId: string): Promise<number> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select('weekly_capacity_hours')
    .eq('id', memberId)
    .single();

  if (error || !data) {
    console.error('Error fetching default capacity:', error);
    return 35; // Default fallback
  }

  return data.weekly_capacity_hours ?? 35;
}

// ─────────────────────────────────────────────────────────────────────────────
// UNASSIGN TASK (via edge function)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Remove an assignee from a task via the unassign-task edge function.
 */
export async function unassignTaskMember(
  orgId: string,
  taskId: string,
  orgMemberId: string
): Promise<void> {
  const { error } = await supabase.functions.invoke('unassign-task', {
    body: { org_id: orgId, task_id: taskId, org_member_id: orgMemberId },
  });

  if (error) throw error;
}
